import {Component, OnInit} from '@angular/core';
import {catchError, delay, map, pluck, switchMap, tap} from 'rxjs/operators';
import {ActivatedRoute} from '@angular/router';
import {BehaviorSubject, Observable, of} from 'rxjs';
import {cache} from '../../../utils';
import {HttpError} from '../../shared/api.service';
import {AuthService} from '../../shared/auth.service';
import {NavigationService} from '../../shared/navigation.service';
import {ClientApiService} from '../client-api.service';
import {PositionStatus} from '../position-search.pipe';
import {QccInfoResponse} from '../../../../app/scripts/services/api';
import {environment} from '../../../environments/environment';
import {AppStateService} from '../../shared/app-state.service';

@Component({
  selector: 'positions-page-decoy',
  templateUrl: './positions-page-decoy.component.html',
  styleUrls: [
    '../styles/paginator.scss',
    '../styles/search-sort.scss',
    '../styles/positions-page.scss',
    './positions-page-decoy.component.scss'
  ]
})
export class PositionsPageDecoyComponent implements OnInit {

  completedTab = environment.flags.positionsCompletedTab;
  closedTab = environment.flags.positionsClosedTab;
  endOfAssignment = environment.flags.endOfAssignment;

  Status = PositionStatus;

  qccOrderId$: Observable<number>;
  qccRating$: Observable<number>;
  qccNeedsVerification$: Observable<boolean>;
  qccInfoResponse$: Observable<QccInfoResponse | null>;
  qccLoading$ = new BehaviorSubject(true);
  colorIndices = [10, 6, 2, 3, 4];

  workingSortOptions = new Map<string, [string, {}]>([
    ['start-desc', ['positions.sort.start-desc', {}]],
    ['start-asc', ['positions.sort.start-asc', {}]],
    ['activity', ['positions.sort.activity', {}]]
  ]);

  constructor(private Api: ClientApiService,
              private AppState: AppStateService,
              private Auth: AuthService,
              private route: ActivatedRoute,
              private Navigation: NavigationService) {
  }

  ngOnInit() {
    this.initializeQccData();
  }

  initializeQccData() {
    this.qccRating$ = this.route.queryParams.pipe(pluck('qccRating'));
    this.qccOrderId$ = this.route.queryParams.pipe(
      map(params => parseInt(params.qccOrderId, 10))
    );

    this.qccNeedsVerification$ = this.route.queryParams.pipe(
      pluck('qccNeedsVerification'),
      map(needsVerification => needsVerification === 'true')
    );

    this.qccInfoResponse$ = this.route.queryParams.pipe(
      delay(0),
      tap(() => this.qccLoading$.next(true)),
      switchMap(params => this.getQccInfo(params)),
      switchMap(qcc => this.saveInitialQccRating(qcc)),
      tap(() => this.qccLoading$.next(false)),
      catchError((err: HttpError) => this.handleError('Failed to retrieve QCC info', err)),
      cache()
    );
  }

  getQccInfo(params: any): Observable<{response, orderId, rating}> {
    return params.qccTrackingId
      ? this.Api.getQccInfo(params.qccTrackingId).pipe(
        tap(response => this.AppState.locale$.next(response.locale)),
        map(response => ({response, orderId: params.qccOrderId, rating: params.qccRating}))
      )
      : of({response: null, orderId: null, rating: null});
  }

  saveInitialQccRating({response, orderId, rating}): Observable<QccInfoResponse> {
    if (response && orderId && rating) {
      const ratedOrder = response.orders.find(order => order.order === parseInt(orderId, 10));

      return this.Api.saveQccReplies(response.token, {
        replies: [{...ratedOrder, rating}],
        companyNotes: response.companyNotes,
        companyRating: response.companyRating
      }, false).pipe(
        tap(ids => ratedOrder.id = ids[0]),
        map(() => response)
      );
    } else {
      return of(null);
    }
  }

  handleError(message: string, err: HttpError): Observable<null> {
    console.error(message, err);
    this.qccLoading$.next(false);

    return this.Navigation.handleError(err) && of(null);
  }
}
